import {
  type ReactNode,
  useCallback,
  useMemo,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  buildPlaygroundConversationDestination,
  buildPlaygroundNewChatDestination,
  buildPlaygroundStandaloneDestination,
  getPlaygroundRouteNamespace,
  type PlaygroundNavigateOptions,
  type PlaygroundRouting,
  PlaygroundRoutingProvider,
} from "./playground-routing";

export const LegacyPlaygroundRoutingProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const location = useLocation();
  const routerNavigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const routeNamespace = getPlaygroundRouteNamespace(location.pathname);
  const newChatDestination =
    buildPlaygroundNewChatDestination(routeNamespace);

  const navigate = useCallback(
    (destination: string, options?: PlaygroundNavigateOptions) => {
      routerNavigate(destination, { replace: options?.replace });
    },
    [routerNavigate],
  );
  const buildConversationDestination = useCallback(
    (conversationId: string) =>
      buildPlaygroundConversationDestination(routeNamespace, conversationId),
    [routeNamespace],
  );
  const navigateToNewChat = useCallback(
    (options?: PlaygroundNavigateOptions) => {
      navigate(newChatDestination, options);
    },
    [navigate, newChatDestination],
  );
  const navigateToConversation = useCallback(
    (conversationId: string, options?: PlaygroundNavigateOptions) => {
      navigate(buildConversationDestination(conversationId), options);
    },
    [buildConversationDestination, navigate],
  );

  const value = useMemo<PlaygroundRouting>(
    () => ({
      id,
      pathname: location.pathname,
      routeNamespace,
      navigate,
      newChatDestination,
      buildConversationDestination,
      buildStandaloneDestination: buildPlaygroundStandaloneDestination,
      navigateToNewChat,
      navigateToConversation,
    }),
    [
      buildConversationDestination,
      id,
      location.pathname,
      navigate,
      navigateToConversation,
      navigateToNewChat,
      newChatDestination,
      routeNamespace,
    ],
  );

  return (
    <PlaygroundRoutingProvider value={value}>
      {children}
    </PlaygroundRoutingProvider>
  );
};
